import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEligibilityData } from "../useEligibilityData";
import { useStageGuard } from "../useStageGuard";
import { usePreventBack } from "../usePreventBack";
import Navbar from "../../../components/molecules/Navbar";
import Footer from "../../../components/molecules/Footer";
import Loader from "../../../components/atoms/Loader";
import IconIncomeDetails from "../../../images/icons/income_details.svg";
import ScrollPicker from "../ScrollPicker";
import { updateEmployment } from "../../../services/application";
import styles from "../index.module.css";

const labelStyle: React.CSSProperties = {
  fontSize: "0.68rem",
  fontWeight: 600,
  color: "#999",
  letterSpacing: "0.7px",
  textTransform: "uppercase",
  marginBottom: "0.3rem",
  display: "block",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.72rem 0.9rem 0.72rem 2.4rem",
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  fontSize: "0.88rem",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "Outfit, sans-serif",
  color: "#333",
  background: "#fff",
};

const iconWrap: React.CSSProperties = {
  position: "absolute",
  left: "0.75rem",
  top: "50%",
  transform: "translateY(-50%)",
  display: "flex",
  alignItems: "center",
  pointerEvents: "none",
};

function EligibilityIncomeDetails() {
  usePreventBack();
  const navigate = useNavigate();

  const userData = useEligibilityData();

  useStageGuard(userData?.userId, userData?.applicationId, 1);

  const [employmentType, setEmploymentType] = useState<"salaried" | "self-employed">("salaried");
  const [employerName, setEmployerName] = useState("");
  const [typeOfBusiness, setTypeOfBusiness] = useState("");
  const [salary, setSalary] = useState(19500);
  const [income, setIncome] = useState(19500);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{ employerName?: string; typeOfBusiness?: string }>({});

  useEffect(() => {
    if (userData?.userId && userData?.applicationId) {
      fetchSummary(userData.userId, userData.applicationId);
    }
  }, []);

  const fetchSummary = async (userId: string, applicationId: string) => {
    try {
      const token = sessionStorage.getItem("eligibility_auth_token");
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const response = await fetch(
        `${process.env.REACT_APP_DASHBOARD_URL}/summary/user?id=${userId}&applicationId=${applicationId}`,
        { headers }
      );
      const result = await response.json();
      const d = result?.data;
      if (d) {
        if (d.employmentType) {
          setEmploymentType(d.employmentType === "self-employed" ? "self-employed" : "salaried");
        }
        if (d.employerName) setEmployerName(d.employerName);
        if (d.typeOfBusiness) setTypeOfBusiness(d.typeOfBusiness);
        if (d.salary) setSalary(d.salary);
        if (d.incomePerMonth) setIncome(d.incomePerMonth);
      }
    } catch {
      // leave defaults if fetch fails
    }
  };

  const navigateByStage = (stage: number) => {
    const navData = userData;
    if (stage === 2) { navigate("/eligibility/bank-statement", { replace: true, state: { data: navData } }); return; }
    else if (stage === 3) { navigate("/eligibility/bank-statement", { replace: true, state: { data: navData } }); return; }
    else if (stage === 4) { navigate("/eligibility/waiting", { replace: true, state: { data: navData, nextRoute: "/eligibility/digilocker" } }); return; }
    else if (stage === 5) { navigate("/eligibility/under-review", { replace: true, state: { data: navData } }); return; }
    else if (stage === 8) { navigate("/eligibility/congratulations", { replace: true, state: { data: navData } }); return; }
    else if (stage === 10) { navigate("/eligibility/waiting", { replace: true, state: { data: navData, fromStage10: true } }); return; }
    else if (stage === 9) { navigate("/eligibility/selfie", { replace: true, state: { data: navData } }); return; }
    else if (stage === 11) { navigate("/eligibility/agreement", { replace: true, state: { data: navData } }); return; }
    else if (stage === 12) { navigate("/eligibility/emandate", { replace: true, state: { data: navData } }); return; }
    else if (stage === 24) { navigate("/eligibility/digilocker", { replace: true, state: { data: navData } }); return; }
    else if (stage === 25) { navigate("/eligibility/evaluating", { replace: true, state: { data: navData } }); return; }
    else if (stage === 18) { navigate("/eligibility/rejected", { replace: true, state: { data: navData } }); return; }
    else { navigate("/eligibility/bank-statement", { replace: true, state: { data: navData } }); }
  };

  const handleSubmit = async () => {
    setError("");
    const errors: { employerName?: string; typeOfBusiness?: string } = {};
    if (employmentType === "salaried" && !employerName.trim()) {
      errors.employerName = "Employer name is required";
    }
    if (employmentType === "self-employed" && !typeOfBusiness.trim()) {
      errors.typeOfBusiness = "Type of business is required";
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setLoading(true);

    const token = sessionStorage.getItem("eligibility_auth_token");

    const payload = {
      userId: userData.userId,
      applicationId: userData.applicationId,
      employmentType,
      employerName: employmentType === "salaried" ? employerName : null,
      salary: employmentType === "salaried" ? salary : 0,
      incomePerMonth: employmentType === "self-employed" ? income : 0,
      typeOfBusiness: employmentType === "self-employed" ? typeOfBusiness : null,
      channelId: 6,
    };

    try {
      const res = await updateEmployment(payload, token);
      if (res && !res.error) {
        try {
          const stageRes = await fetch(
            `${process.env.REACT_APP_DASHBOARD_URL}/admin/application/stage?applicationId=${userData.applicationId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const stageData = await stageRes.json();
          const stage = stageData?.data?.stage;
          navigateByStage(stage);
        } catch {
          navigate("/eligibility/bank-statement", { state: { data: userData } });
        }
      } else {
        setError(res?.message || "Oops! There is an issue at our end. Please contact us.");
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message;
      setError(msg || "Oops! There is an issue at our end. Please contact us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.body}>
      <div className={styles.backdrop}>
        <Navbar />
        <div className={styles.main}>
          <div className={styles.cardWrapper}>
            <div className={styles.container}>
              <div className={styles.cardHeader}>
                <img src={IconIncomeDetails} alt="" />
                <p className={styles.cardTitle}>Income Details</p>
              </div>

              {/* ── Employment Type toggle ── */}
              <div style={{ paddingTop: "0.5rem", paddingBottom: "0.4rem" }}>
                <span style={labelStyle}>Employment Status</span>
                <div style={{
                  display: "flex",
                  background: "#f0f0f0",
                  borderRadius: "12px",
                  padding: "0.25rem",
                  marginTop: "0.3rem",
                }}>
                  {(["salaried", "self-employed"] as const).map((type) => (
                    <div
                      key={type}
                      onClick={() => { setEmploymentType(type); setFieldErrors({}); }}
                      style={{
                        flex: 1,
                        textAlign: "center",
                        padding: "0.6rem 0.4rem",
                        borderRadius: "10px",
                        fontSize: "0.88rem",
                        fontWeight: employmentType === type ? 700 : 500,
                        color: employmentType === type ? "#d32028" : "#888",
                        background: employmentType === type ? "#fff" : "transparent",
                        boxShadow: employmentType === type ? "0 1px 4px rgba(0,0,0,0.12)" : "none",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {type === "salaried" ? "Salaried" : "Self-Employed"}
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.content} style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {employmentType === "salaried" ? (
                  <>
                    <div>
                      <span style={labelStyle}>Employer Name</span>
                      <div style={{ position: "relative" }}>
                        <span style={iconWrap}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="7" width="18" height="14" rx="2" stroke="#c0c0c0" strokeWidth="1.8" />
                            <path d="M8 7V5C8 3.9 8.9 3 10 3H14C15.1 3 16 3.9 16 5V7" stroke="#c0c0c0" strokeWidth="1.8" strokeLinejoin="round" />
                            <path d="M3 13H21" stroke="#c0c0c0" strokeWidth="1.5" />
                            <path d="M10 13V15M14 13V15" stroke="#c0c0c0" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                        </span>
                        <input
                          style={{ ...inputStyle, borderColor: fieldErrors.employerName ? "#d32028" : "#e0e0e0" }}
                          placeholder="e.g. Aeon Global Industries"
                          value={employerName}
                          onChange={(e) => { setEmployerName(e.target.value); setFieldErrors((p) => ({ ...p, employerName: undefined })); }}
                        />
                      </div>
                      {fieldErrors.employerName && (
                        <p style={{ color: "#d32028", fontSize: "0.78rem", marginTop: "0.3rem", marginBottom: 0 }}>
                          {fieldErrors.employerName}
                        </p>
                      )}
                    </div>
                    <div>
                      <span style={labelStyle}>Monthly Net Salary</span>
                      <ScrollPicker value={salary} onChange={setSalary} />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <span style={labelStyle}>Type of Business</span>
                      <div style={{ position: "relative" }}>
                        <span style={iconWrap}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <rect x="2" y="7" width="20" height="14" rx="2" stroke="#c0c0c0" strokeWidth="1.8" />
                            <path d="M8 7V5C8 3.9 8.9 3 10 3H14C15.1 3 16 3.9 16 5V7" stroke="#c0c0c0" strokeWidth="1.8" strokeLinejoin="round" />
                          </svg>
                        </span>
                        <input
                          style={{ ...inputStyle, borderColor: fieldErrors.typeOfBusiness ? "#d32028" : "#e0e0e0" }}
                          placeholder="e.g. Retail / Consulting"
                          value={typeOfBusiness}
                          onChange={(e) => { setTypeOfBusiness(e.target.value); setFieldErrors((p) => ({ ...p, typeOfBusiness: undefined })); }}
                        />
                      </div>
                      {fieldErrors.typeOfBusiness && (
                        <p style={{ color: "#d32028", fontSize: "0.78rem", marginTop: "0.3rem", marginBottom: 0 }}>
                          {fieldErrors.typeOfBusiness}
                        </p>
                      )}
                    </div>
                    <div>
                      <span style={labelStyle}>Monthly Income</span>
                      <ScrollPicker value={income} onChange={setIncome} />
                    </div>
                  </>
                )}
              </div>

              {error && (
                <p style={{ color: "#d32028", fontSize: "0.85rem", marginTop: "0.75rem", marginBottom: 0 }}>
                  {error}
                </p>
              )}
              {loading && <Loader />}
            </div>

            <div style={{ padding: "0rem 1.25rem 1rem", display: "flex", flexDirection: "row", alignItems: "center",justifyContent:"center", gap: "0.6rem" }}>
              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{
                  width: "65%",
                  padding: "0.8rem 1rem",
                  background: loading ? "#aaa" : "#d32028",
                  color: "#fff",
                  border: "none",
                  borderRadius: "60px",
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  fontFamily: "Outfit, sans-serif",
                  cursor: loading ? "not-allowed" : "pointer",
                  letterSpacing: "0.3px",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                }}
              >
                {loading ? "Please wait..." : (
                  <>
                    Save & Continue
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12H19M13 6L19 12L13 18" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </>
                )}
              </button>
                {process.env.REACT_APP_CURRENT !== 'prod' && (
                  <button
                    onClick={() => navigate("/eligibility/bank-statement", { state: { data: userData } })}
                    style={{ background: "none", border: "none", color: "#bbb", fontSize: "0.78rem", cursor: "pointer", fontFamily: "Outfit, sans-serif", textDecoration: "underline" }}
                  >
                    Skip
                  </button>
                )}
            </div>
          <Footer light />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EligibilityIncomeDetails;
